import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Colors,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import _ from 'lodash';
import { useRef } from 'react';
import { useDispatch } from 'react-redux';

import { storeDashboardAnalysis } from '../service/widget.service';
import { setChartClickResponse } from '../../../../redux/slices/page-header.slice';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Colors
);

const MAX_BARS_WITH_VALUES = 25;

const BarChartComponent = ({
  cubeResponse,
  annotation,
  dashboardDetails,
  dashboardLogDetails,
  formikData,
  valueAnnotation,
}) => {
  const labels = [];
  const values = [];

  const dispatch = useDispatch();
  if (cubeResponse && cubeResponse.length) {
    const copiedResponse = _.cloneDeep(cubeResponse);

    for (const item of copiedResponse) {
      for (const k of Object.keys(item)) {
        if (
          k?.includes('avg') ||
          k?.includes('sum') ||
          k?.includes('count') ||
          k?.includes('min') ||
          k?.includes('max') ||
          k?.includes('number')
        ) {
          values.push(
            typeof item[k] === 'number'
              ? Number.isInteger(item[k])
                ? item[k]
                : +item[k].toFixed(3)
              : typeof item[k] === 'string' && !Number.isNaN(Number(item[k]))
                ? Number.isInteger(Number(item[k]))
                  ? Number(item[k])
                  : +Number(item[k]).toFixed(3)
                : item[k]
          );

          delete item[k];

          labels.push(
            Object.keys(item)
              .filter(f => f !== undefined)
              .map(record => {
                let formatter = null;

                Object.keys(item).forEach(i => {
                  if (annotation?.[i]?.meta?.format) {
                    formatter = eval(annotation[i].meta.format);
                  }
                });

                return formatter ? formatter(item[record]) : item[record];
              })
              .join(' | ')
          );
        }
      }
    }
  }
  const handleBarClick = (event, elements, chart) => {
    if (!elements.length) return;

    const { index } = elements[0];

    dispatch(setChartClickResponse(cubeResponse?.[index]));
    setTimeout(() => {
      const parentCard = chart?.canvas?.closest('.chartClick');
      parentCard.dataset.fromChart = 'true';
      if (parentCard) {
        parentCard.click();
      }
    }, 0);
  };

  const getContentLength = value => {
    if (value == null) return 0;

    const str = String(value);

    const alphanumeric = str.match(/[a-zA-Z0-9]/g);
    return (alphanumeric && alphanumeric.length === 1) || false;
  };

  const shouldShowBarValues =
    values.length <= MAX_BARS_WITH_VALUES || values.some(getContentLength);

  const primaryColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--primary-color')
    .trim();

  const lightenColor = (hex, percent) => {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);

    const r = Math.min(255, (num >> 16) + amt);
    const g = Math.min(255, ((num >> 8) & 0x00ff) + amt);
    const b = Math.min(255, (num & 0x0000ff) + amt);

    return `rgb(${r}, ${g}, ${b})`;
  };

  const createGradient = ctx => {
    const gradient = ctx.createLinearGradient(0, 0, 0, ctx.canvas.height);
    gradient.addColorStop(0, lightenColor(primaryColor, 30));
    gradient.addColorStop(1, primaryColor);
    return gradient;
  };
  const data = {
    labels,
    datasets: [
      {
        data: values,
        borderWidth: 0,
        backgroundColor: context =>
          formikData?.custom_properties?.chart_color ||
          createGradient(context.chart.ctx),
      },
    ],
  };
  const truncate = (text, width) => {
    let textLength = width < 20 ? 3 : 4;
    let truncateLength = width < 20 ? 2 : 3;

    return text.length > textLength && width < 40
      ? text.slice(0, truncateLength) + '…'
      : text;
  };

  const formatWithAnnotation = value => {
    let formatter = null;

    if (valueAnnotation) {
      Object.keys(valueAnnotation).forEach(key => {
        if (valueAnnotation?.[key]?.meta?.format) {
          formatter = eval(valueAnnotation[key].meta.format);
        }
      });
    }

    return formatter ? formatter(value) : value;
  };

  const barValuePlugin = {
    id: 'barValuePlugin',
    afterDatasetsDraw(chart) {
      if (!shouldShowBarValues) return;

      const { ctx } = chart;

      chart.data.datasets.forEach((dataset, datasetIndex) => {
        const meta = chart.getDatasetMeta(datasetIndex);

        meta.data.forEach((bar, index) => {
          const rawValue = dataset.data[index];
          if (rawValue == null) return;

          if (bar.width < 3) {
            return;
          }
          const formattedValue = formatWithAnnotation(rawValue);
          const displayValue = truncate(String(formattedValue), bar.width);
          const barCount = values.length;

          if (
            barCount > MAX_BARS_WITH_VALUES &&
            !getContentLength(formattedValue) &&
            bar.width < 10
          ) {
            return;
          }

          ctx.save();
          ctx.font = 'bold 10px sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'bottom';
          ctx.fillText(displayValue, bar.x, bar.y);
          ctx.restore();
        });
      });
    },
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    rotation: 270,
    circumference: 180,
    plugins: {
      legend: { display: false },
      colors: {
        enabled: false,
        forceOverride: true,
      },
      tooltip: {
        enabled: true,
        displayColors: false,
        callbacks: {
          label: context => formatWithAnnotation(context.raw),
        },
      },
    },
    scales: {
      x: {
        offset: true,
      },
      y: {
        beginAtZero: true,
      },
    },
    layout: {
      padding: {
        top: 20,
      },
    },
    onClick: handleBarClick,
  };

  if (dashboardDetails && dashboardLogDetails) {
    storeDashboardAnalysis({
      ...dashboardLogDetails,
      ...dashboardDetails,
      executionTime: new Date().getTime() - dashboardDetails.start_time,
      end_time: new Date().getTime(),
    });
  }
  const chartRef = useRef(null);
  return (
    <div
      onClick={e => {
        const chart = chartRef.current;
        if (!chart) {
          dispatch(setChartClickResponse(null));
          return;
        }

        const elements = chart.getElementsAtEventForMode(
          e.nativeEvent,
          'nearest',
          { intersect: true },
          true
        );

        if (elements.length) {
          e.stopPropagation();
          return;
        }

        dispatch(setChartClickResponse(null));
      }}
      className="w-full h-full"
    >
      <Bar
        data={data}
        ref={chartRef}
        options={options}
        plugins={shouldShowBarValues ? [barValuePlugin] : []}
      />
    </div>
  );
};

export default BarChartComponent;
